"use client";

import React from "react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage, FormDescription } from "@/components/ui/form";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Loader, CircleCheck as CheckCircle2, CircleAlert as AlertCircle, ArrowRight } from 'lucide-react';
import { Link } from "@/components/common/Link";
export default function AgentReferralProgram() {
  return <section id="agent-referral-program" className="bg-background py-16 md:py-24 lg:py-32">
      <div className="container mx-auto px-4 md:px-6 max-w-5xl">
        <div className="mb-12 md:mb-16 max-w-3xl">
          <div className="inline-flex items-center rounded-lg border border-border px-2.5 py-0.5 text-xs font-semibold text-muted-foreground mb-6">
            06 / Partners
          </div>
          <h2 className="font-default text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight text-foreground mb-4">
            Agent Referral Program
          </h2>
          <p className="text-lg text-muted-foreground leading-relaxed">
            Partner with us to provide your clients with exceptional service. Submit your prospect's details below, and our team will handle the rest while keeping you informed every step of the way.
          </p>
        </div>

        <div className="bg-card border border-border rounded-xl shadow-sm overflow-hidden">
          <FormProvider formId="agent-referral-form" sectionName="Agent Referral Program" defaultValues={{
          prospectName: "",
          prospectEmail: "",
          prospectPhone: "",
          prospectService: "",
          agentName: "",
          agentAgency: "",
          agentEmail: "",
          agentPhone: ""
        }} validationRules={{
          prospectName: {
            required: true,
            minLength: 2
          },
          prospectEmail: {
            required: true,
            pattern: "email"
          },
          prospectPhone: {
            required: true,
            minLength: 10
          },
          prospectService: {
            required: true
          },
          agentName: {
            required: true,
            minLength: 2
          },
          agentAgency: {
            required: true
          },
          agentEmail: {
            required: true,
            pattern: "email"
          },
          agentPhone: {
            required: true,
            minLength: 10
          }
        }} submitText="Submit Referral" successMessage="Referral submitted successfully. Our team will contact you shortly." className="p-6 md:p-8 lg:p-10">
            <ReferralForm />
          </FormProvider>
        </div>
      </div>
    </section>;
}
function ReferralForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center py-12 text-center space-y-6 animate-in fade-in zoom-in duration-500">
        <div className="size-16 bg-green-100 dark:bg-green-900/30 rounded-lg flex items-center justify-center mb-4">
          <CheckCircle2 className="size-8 text-green-600 dark:text-green-400" />
        </div>
        <h3 className="font-default text-2xl font-semibold text-foreground">
          Thank You for Your Referral
        </h3>
        <p className="text-muted-foreground max-w-md mx-auto">
          {successMessage}
        </p>
        <Button onClick={resetForm} variant="outline" className="mt-4">
          Submit Another Referral
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit}>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16">
        {}
        <div className="space-y-6">
          <div>
            <h3 className="font-default text-xl font-semibold text-foreground mb-1">
              Prospect Details
            </h3>
            <p className="text-sm text-muted-foreground">
              Information about the client you are referring.
            </p>
          </div>
          
          <Separator />

          <div className="space-y-5">
            <FormField control={control} name="prospectName" rules={{
            required: "Prospect name is required"
          }} render={({
            field
          }) => <FormItem>
                  <FormLabel>Full Name</FormLabel>
                  <FormControl>
                    <Input placeholder="John Doe" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>} />

            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <FormField control={control} name="prospectEmail" rules={{
              required: "Email is required",
              pattern: {
                value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                message: "Invalid email address"
              }
            }} render={({
              field
            }) => <FormItem>
                    <FormLabel>Email Address</FormLabel>
                    <FormControl>
                      <Input type="email" placeholder="john@example.com" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>} />

              <FormField control={control} name="prospectPhone" rules={{
              required: "Phone number is required"
            }} render={({
              field
            }) => <FormItem>
                    <FormLabel>Phone Number</FormLabel>
                    <FormControl>
                      <Input type="tel" placeholder="+1 (555) 000-0000" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>} />
            </div>

            <FormField control={control} name="prospectService" rules={{
            required: "Please select a service of interest"
          }} render={({
            field
          }) => <FormItem>
                  <FormLabel>Service of Interest</FormLabel>
                  <Select onValueChange={field.onChange} defaultValue={field.value}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select a service area" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="real-estate">Real Estate Acquisition</SelectItem>
                      <SelectItem value="resale">Resale</SelectItem>
                      <SelectItem value="property-management">Property Management</SelectItem>
                      <SelectItem value="hospitality">Hospitality Services</SelectItem>
                      <SelectItem value="exit-strategy">Exit Strategy Consulting</SelectItem>
                      <SelectItem value="other">Other / Unsure</SelectItem>
                    </SelectContent>
                  </Select>
                  <FormDescription>
                    What specific services is your prospect looking for?
                  </FormDescription>
                  <FormMessage />
                </FormItem>} />
          </div>
        </div>

        {}
        <div className="space-y-6">
          <div>
            <h3 className="font-default text-xl font-semibold text-foreground mb-1">
              Referring Agent
            </h3>
            <p className="text-sm text-muted-foreground">
              Your contact information for follow-up and commission.
            </p>
          </div>
          
          <Separator />

          <div className="space-y-5">
            <FormField control={control} name="agentName" rules={{
            required: "Agent name is required"
          }} render={({
            field
          }) => <FormItem>
                  <FormLabel>Your Full Name</FormLabel>
                  <FormControl>
                    <Input placeholder="Jane Smith" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>} />

            <FormField control={control} name="agentAgency" rules={{
            required: "Agency/Brokerage name is required"
          }} render={({
            field
          }) => <FormItem>
                  <FormLabel>Agency / Brokerage</FormLabel>
                  <FormControl>
                    <Input placeholder="Premier Real Estate Group" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>} />

            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <FormField control={control} name="agentEmail" rules={{
              required: "Email is required",
              pattern: {
                value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                message: "Invalid email address"
              }
            }} render={({
              field
            }) => <FormItem>
                    <FormLabel>Professional Email</FormLabel>
                    <FormControl>
                      <Input type="email" placeholder="jane@agency.com" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>} />

              <FormField control={control} name="agentPhone" rules={{
              required: "Phone number is required"
            }} render={({
              field
            }) => <FormItem>
                    <FormLabel>Direct Phone</FormLabel>
                    <FormControl>
                      <Input type="tel" placeholder="+1 (555) 111-2222" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>} />
            </div>
          </div>
        </div>
      </div>

      <div className="mt-10 pt-8 border-t border-border flex flex-col md:flex-row items-center justify-between gap-6">
        <p className="text-sm text-muted-foreground max-w-md">
          By submitting this form, you agree to our{" "}
          <Link to="#" className="text-primary hover:underline underline-offset-4">
            Terms & Conditions
          </Link>{" "}
          regarding referral partnerships and commission structures.
        </p>

        <div className="w-full md:w-auto flex flex-col items-end gap-4">
          {submitError && <Alert variant="destructive" className="w-full md:w-auto max-w-md">
              <AlertCircle className="size-4" />
              <AlertTitle>Submission Failed</AlertTitle>
              <AlertDescription>{submitError}</AlertDescription>
            </Alert>}
          
          <Button type="submit" size="lg" disabled={isSubmitting} className="w-full md:w-auto min-w-[200px]">
            {isSubmitting ? <>
                <Loader className="animate-spin mr-2 size-4" />
                Processing...
              </> : <>
                Submit Referral
                <ArrowRight className="ml-2 size-4" />
              </>}
          </Button>
        </div>
      </div>
    </Form>;
}